import React from 'react';


const getValueToLevel = value => {
  if (value < 0.2) {
    return { id: 'MA', name: 'Muy alto' };
  }
  if (value < 0.4) {
    return { id: 'A', name: 'Alto' };
  }
  if (value < 0.6) {
    return { id: 'M', name: 'Moderado' };
  }
  if (value < 0.8) {
    return { id: 'B', name: 'Bajo' };
  }
  return { id: 'MB', name: 'Muy bajo' };
};

// Mapeo completo de campos del backend a títulos para todos los componentes
const concernsMapping = {
  // Land (Tierra)
  naturalVegetationCoverage: 'Proporción de vegetación natural',

  // Biodiversity (Biodiversidad)
  nativeSpeciesAbundance: 'Abundancia de especies nativas',
  ecosystemIntegrity: 'Integridad de los ecosistemas',

  // Freshwater (Agua dulce)
  freshwaterQuality: 'Calidad de agua dulce',
  freshwaterAvailability: 'Disponibilidad de agua dulce',
};

// Función genérica para procesar datos del backend y generar concerns
const processConcernsData = (indicatorsData, fieldsToProcess, targetYear = 2024, alwaysPartialFields = []) => {
  const processedConcerns = [];

  fieldsToProcess.forEach(field => {
    const isAlwaysPartial = alwaysPartialFields.includes(field);

    if (indicatorsData[field] && Array.isArray(indicatorsData[field])) {
      // Buscar datos del año objetivo
      const yearData = indicatorsData[field].find(item => item.year === targetYear);

      if (yearData && typeof yearData.value === 'number') {
        // Hay datos para el año
        processedConcerns.push({
          name: concernsMapping[field],
          value: yearData.value,
          level: getValueToLevel(yearData.value),
          isPartial: isAlwaysPartial,
          fieldKey: field,
        });
      } else {
        processedConcerns.push({
          name: concernsMapping[field],
          value: null,
          level: { id: 'ND', name: 'Sin datos' }, // Valor por defecto
          isPartial: false,
          fieldKey: field,
        });
      }
    } else {
      processedConcerns.push({
        name: concernsMapping[field],
        value: null,
        level: { id: 'ND', name: 'Sin datos' },
        isPartial: false,
        fieldKey: field,
      });
    }
  });

  return processedConcerns;
};

// Configuraciones específicas por componente
const componentConfig = {
  land: {
    fields: [ 'naturalVegetationCoverage' ],
    indicators: {
      naturalVegetationCoverage: {
        shortName: 'PVN',
        fullName: 'Proporción de Vegetación Natural',
        description: 'score',
        tooltip: `Es una métrica que cuantifica el porcentaje de la superficie total que está cubierta por vegetación
          natural en comparación con otras clases de superficie. Las clases que componen la vegetación natural
          corresponden a Bosques, Matorrales, Praderas y Humedales.`,
        url: 'https://www.naturepositive.org/app/uploads/2026/02/Supporting-Information_Draft-Measurement-Guidance-Executive-Summary_Feb2026.pdf',
      },
    },
  },
  biodiversity: {
    fields: [ 'nativeSpeciesAbundance', 'ecosystemIntegrity' ],
    indicators: {
      nativeSpeciesAbundance: {
        shortName: 'AEN',
        fullName: 'Abundancia de Especies Nativas',
        description: 'índice',
        tooltip: `Suma de individios de todas las especies nativas presentes. Este índice se obtiene a partir
          de muestreos en terreno, dispositivos (camaras trampa), registros y modelación.`,
        url: null,
      },
      ecosystemIntegrity: {
        shortName: 'EII',
        fullName: 'Ecosystem Integrity Index',
        description: 'indicador',
        tooltip: (
          <>
            <p>
              El Ecosystem Integrity Index (EII) es un indicador compuesto que resume el estado de un ecosistema
              en un único valor entre 0 y 1, donde 0 representa la mayor degradación respecto de una condición
              de referencia y 1 indica la mayor integridad ecológica. El índice integra tres componentes clave
              de la integridad ecológica: función, composición y estructura.
            </p>
            <p>
              Estos componentes pueden estimarse mediante distintas métricas, como PPB o PPN (función),
              riqueza de especies o BII (composición) y métricas de fragmentación del hábitat (estructura).
            </p>
            <p>
              Las métricas utilizadas para calcular el EII deben ser pertinentes al tipo de ecosistema
              analizado. Por lo tanto, el procedimiento general es:
            </p>
            <ol>
              <li>Seleccionar y estimar una métrica para cada uno de los componentes.</li>
              <li>Normalizar cada métrica a una escala de 0 a 1.</li>
              <li>Integrar las tres métricas mediante el procedimiento descrito en Hall et al., 2022.</li>
            </ol>
          </>
        ),
        url: 'https://www.biorxiv.org/content/10.1101/2022.08.21.504707v2',
      },
    },
  },
  freshwater: {
    fields: [ 'freshwaterQuality', 'freshwaterAvailability' ],
    alwaysPartialFields: [ 'freshwaterAvailability' ],
    indicators: {
      freshwaterQuality: {
        shortName: 'CAD',
        fullName: 'Calidad de Agua Dulce',
        description: 'índice',
        tooltip: `La calidad del agua en cuerpos de agua dulce puede ser evaluada a partir de la turbidez
          o la concentración de sedimentos en suspensión. Con este fin, se utiliza el índice NDTI
          (Normalized Difference Turbidity Index), que se construye a partir de imágenes satelitales.`,
        url: 'https://seadas.gsfc.nasa.gov/help-9.0.0/ndti/NdtiAlgorithmSpecification.html',
      },
      freshwaterAvailability: {
        shortName: 'DAD',
        fullName: 'Disponibilidad de Agua Dulce',
        description: 'indicador',
        tooltip: `Superficie cubierta por cuerpos de agua, normalizada entre 0 y 1 respecto al año de su mayor extensión desde el 2020.`,
        url: null,
      },
    },
  },
};

// Función para procesar concerns específicos de un componente
const processComponentConcerns = (indicatorsData, componentType, targetYear = 2024) => {
  const config = componentConfig[componentType];
  if (!config) {
    throw new Error(`Tipo de componente no soportado: ${componentType}`);
  }

  return processConcernsData(
    indicatorsData,
    config.fields,
    targetYear,
    config.alwaysPartialFields || [],
  );
};


export {
  getValueToLevel,
  concernsMapping,
  processConcernsData,
  processComponentConcerns,
  componentConfig,
};
